import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useNotify } from 'utils/useNotify'
import I18n from 'i18n'
import { t } from 'i18n'
import clsx from 'clsx'
import { Grid, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SignalWifi1BarIcon from '@material-ui/icons/SignalWifi1Bar'
import SignalWifi4BarIcon from '@material-ui/icons/SignalWifi4Bar'
import Button from 'components/button'
import Loading from 'components/loading'
import { commonStyles, fetchJson, SignUp, useSignUp } from './common'

const POLL_INTERVAL_MS = 3000

// TODO: Re-enable tutorial when parts of system described in tutorial are ready.
export const DISABLE_ONBOARDING = process.env.RAILS_ENV !== 'test'

const useStyles = makeStyles((theme) => ({
  ...commonStyles(theme),
  fullHeight: {
    height: '100%'
  },
  mainSubtitle: {
    marginTop: theme.spacing(1)
  },
  icon: {
    borderRadius: theme.shape.borderRadius * 8,
    padding: theme.spacing(2),
    background: theme.palette.common.white,
    boxShadow: '0px 0px 30px 0px rgb(0 0 0 / 10%)',
    width: 60,
    display: 'inline-block',
    textAlign: 'center'
  },
  title: {
    marginLeft: theme.spacing(2),
    lineHeight: '60px',
    verticalAlign: 'top'
  },
  description: {
    color: theme.palette.inputs.label.color.light,
    textAlign: 'justify',
    margin: theme.spacing(3, 0)
  },
  loadingText: {
    textAlign: 'center',
    marginTop: theme.spacing(3)
  }
}))

const DatasetOption: React.FC<{
  handlePickDataset: () => unknown
  isSubmitting: boolean
  IconComponent: React.ElementType
  title: string
  description: string
  buttonText: string
}> = ({ handlePickDataset, isSubmitting, IconComponent, title, description, buttonText }) => {
  const classes = useStyles()

  return (
    <Grid item xs={12} sm={6}>
      <Paper className={clsx(classes.formPaper, classes.fullHeight)} elevation={0}>
        <Grid container direction='column' justifyContent='space-between' alignItems='stretch' className={classes.fullHeight}>
          <Grid item>
            <div>
              <span className={classes.icon}>
                <IconComponent />
              </span>
              <Typography component='span' variant='h2' className={classes.title}>
                {title}
              </Typography>
            </div>
            <Typography component='p' className={classes.description}>
              {description}
            </Typography>
          </Grid>
          <Grid item>
            <div className={classes.buttons}>
              <Button
                variant='contained'
                color='primary'
                className={classes.submit}
                disabled={isSubmitting}
                size='large'
                onClick={handlePickDataset}
              >
                {buttonText}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  )
}

export const DatasetForm: React.FC = () => {
  const classes = useStyles()
  const { notifyError } = useNotify()
  const { signUp, setSignUp } = useSignUp()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [provisioningFailed, setProvisioningFailed] = useState(false)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isProvisioning = isSubmitting || signUp.step === 'provisioning_dataset'
  const hasFailed = provisioningFailed || signUp.step === 'provisioning_failed'

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  const startPolling = useCallback(() => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const status = await fetchJson<SignUp>(`/${I18n.locale}/complete_sign_up/current`)
        if (status.step === 'completed') {
          stopPolling()
          if (DISABLE_ONBOARDING) {
            window.location.assign('/complete_sign_up/open_subdomain')
          } else {
            setIsSubmitting(false)
            setSignUp(status)
          }
        } else if (status.step === 'provisioning_failed') {
          stopPolling()
          setIsSubmitting(false)
          setProvisioningFailed(true)
          notifyError({ message: t('sign_up.dataset_form.provisioning_failed') })
        }
      } catch {
        // Polling error — keep retrying, the interval will fire again
      }
    }, POLL_INTERVAL_MS)
  }, [stopPolling, setSignUp, notifyError])

  useEffect(() => {
    if (signUp.step === 'provisioning_dataset') {
      startPolling()
    }
    return () => stopPolling()
  }, [signUp.step]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePickDataset = async (dataset: string) => {
    try {
      setIsSubmitting(true)
      setProvisioningFailed(false)
      await fetchJson<SignUp>(`/${I18n.locale}/complete_sign_up/select_dataset`, {
        method: 'PATCH',
        bodyHash: { dataset },
        headers: {
          'Content-Type': 'application/json'
        }
      })
      startPolling()
    } catch (error) {
      setIsSubmitting(false)
      let message: string | undefined
      if (error?.isValidationError) {
        const freshSignUp: SignUp = await error?.response.json()
        setSignUp(freshSignUp)
        message = freshSignUp.errors
      } else {
        message = ''
      }
      notifyError({ message })
    }
  }

  if (hasFailed) {
    return (
      <Grid container direction='column' alignItems='center'>
        <Grid item className={classes.loadingText}>
          <Typography variant='h5'>{t('sign_up.dataset_form.provisioning_failed')}</Typography>
        </Grid>
      </Grid>
    )
  }

  if (isProvisioning) {
    return (
      <Grid container direction='column' alignItems='center'>
        <Grid item className={classes.loadingText}>
          <Typography variant='h5'>
            {t('sign_up.dataset_form.populating_data_thanks')}
            <br />
            {t('sign_up.dataset_form.populating_data_explanation')}
          </Typography>
        </Grid>
        <Grid item>
          <Loading />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container spacing={5}>
      <Grid item xs={12}>
        <Typography variant='h3'>{t('sign_up.dataset_form.title')}</Typography>
        <Typography component='p' className={classes.mainSubtitle}>
          {t('sign_up.dataset_form.subtitle')}
        </Typography>
      </Grid>

      <DatasetOption
        handlePickDataset={() => handlePickDataset('full')}
        isSubmitting={isSubmitting}
        IconComponent={SignalWifi4BarIcon}
        title={t('sign_up.dataset_form.full_dataset_title')}
        description={t('sign_up.dataset_form.full_dataset_description')}
        buttonText={t('sign_up.dataset_form.full_dataset_button')}
      />

      <DatasetOption
        handlePickDataset={() => handlePickDataset('minimal')}
        isSubmitting={isSubmitting}
        IconComponent={SignalWifi1BarIcon}
        title={t('sign_up.dataset_form.minimal_dataset_title')}
        description={t('sign_up.dataset_form.minimal_dataset_description')}
        buttonText={t('sign_up.dataset_form.minimal_dataset_button')}
      />
    </Grid>
  )
}
